import { lazy } from "react";
import { Route, Navigate, useParams, useLocation } from "react-router-dom";
import CategoryFiltersPage from "../pages/category/CategoryFiltersPage";
import CategoriesPage from "../pages/category/CategoriesPage";
import ProductDetailsPage from "../pages/product/ProductDetailsPage";
import SearchResultsPage from "../pages/search/SearchResultsPage";
import UserSearchPage from "../pages/users/UserSearchPage";
import ComparisonPage from "../pages/compare/ComparisonPage";
import AboutPage from "../pages/static/AboutPage";
import ContactPage from "../pages/static/ContactPage";
import PrivacyPolicyPage from "../pages/static/PrivacyPolicyPage";
import TermsPage from "../pages/static/TermsPage";
import PublicDecisionPage from "../pages/decision/PublicDecisionPage";
import UserLoginPage from "../pages/auth/UserLoginPage";
import ShoppingProfileOnboardingPage from "../pages/onboarding/ShoppingProfileOnboardingPage";
import ForgotPasswordPage from "../pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "../pages/auth/ResetPasswordPage";
import UserProfileLayout from "../pages/user/UserProfileLayout";
import ProfileToUserRedirect from "./ProfileToUserRedirect";
import FeedPage from "../pages/feed/FeedPage";
import PostDetailsPage from "../pages/post/PostDetailsPage";
import FAQPage from "../pages/static/FAQPage";
import RequireUserAuth from "../components/auth/RequireUserAuth";
import RequirePublicPage from "../components/auth/RequirePublicPage";
import MarketplaceErrorBoundary from "../components/marketplace/MarketplaceErrorBoundary";

const MyListingsPage = lazy(() => import("../pages/user/sections/MyListingsPage"));
const CreateListingPage = lazy(
  () => import("../pages/user/sections/CreateListingPage"),
);
const MarketplacePage = lazy(
  () => import("../pages/marketplace/MarketplacePage"),
);
const ListingDetailPage = lazy(
  () => import("../pages/marketplace/ListingDetailPage"),
);
const SavedListingsPage = lazy(
  () => import("../pages/marketplace/SavedListingsPage"),
);
const SellerStorefrontPage = lazy(
  () => import("../pages/marketplace/SellerStorefrontPage"),
);
const MarketplaceMessagesPage = lazy(
  () => import("../pages/marketplace/MarketplaceMessagesPage"),
);
const MarketplaceReviewsPage = lazy(
  () => import("../pages/marketplace/MarketplaceReviewsPage"),
);
const MarketplaceSavedSearchesPage = lazy(
  () => import("../pages/marketplace/MarketplaceSavedSearchesPage"),
);

const PostRedirect = () => {
  const { postId } = useParams();
  return <Navigate to={`/post/${postId}`} replace />;
};

const LegacyHomeRedirect = () => {
  const location = useLocation();
  return (
    <Navigate
      to={{ pathname: "/", hash: location.hash, search: location.search }}
      replace
    />
  );
};

export default function publicRouteElements() {
  return (
    <>
      <Route path="/" element={<CategoriesPage />} />
      <Route path="/home" element={<LegacyHomeRedirect />} />
      <Route path="/categories" element={<Navigate to="/" replace />} />
      <Route path="/homepage" element={<Navigate to="/" replace />} />
      <Route path="/category/:categoryId" element={<CategoryFiltersPage />} />
      <Route path="/product/:id" element={<ProductDetailsPage />} />
      <Route path="/products/:id" element={<ProductDetailsPage />} />
      <Route
        path="/category/:categoryId/product/:id"
        element={<ProductDetailsPage />}
      />
      <Route
        path="/post/:postId"
        element={
          <RequirePublicPage pageKey="feed">
            <PostDetailsPage />
          </RequirePublicPage>
        }
      />
      <Route
        path="/posts/:postId"
        element={
          <RequirePublicPage pageKey="feed">
            <PostRedirect />
          </RequirePublicPage>
        }
      />
      <Route path="/search" element={<SearchResultsPage />} />
      <Route path="/d/:shareToken" element={<PublicDecisionPage />} />
      <Route
        path="/compare"
        element={
          <RequirePublicPage pageKey="compare">
            <ComparisonPage />
          </RequirePublicPage>
        }
      />
      <Route
        path="/marketplace"
        element={
          <RequirePublicPage pageKey="marketplace">
            <MarketplaceErrorBoundary>
              <MarketplacePage />
            </MarketplaceErrorBoundary>
          </RequirePublicPage>
        }
      />
      <Route
        path="/marketplace/new"
        element={
          <RequirePublicPage pageKey="marketplace">
            <RequireUserAuth>
              <MarketplaceErrorBoundary>
                <CreateListingPage />
              </MarketplaceErrorBoundary>
            </RequireUserAuth>
          </RequirePublicPage>
        }
      />
      <Route
        path="/marketplace/:id/edit"
        element={
          <RequirePublicPage pageKey="marketplace">
            <RequireUserAuth>
              <MarketplaceErrorBoundary>
                <CreateListingPage />
              </MarketplaceErrorBoundary>
            </RequireUserAuth>
          </RequirePublicPage>
        }
      />
      <Route
        path="/marketplace/my-listings"
        element={
          <RequirePublicPage pageKey="marketplace">
            <RequireUserAuth>
              <MarketplaceErrorBoundary>
                <MyListingsPage />
              </MarketplaceErrorBoundary>
            </RequireUserAuth>
          </RequirePublicPage>
        }
      />
      <Route
        path="/marketplace/saved"
        element={
          <RequirePublicPage pageKey="marketplace">
            <MarketplaceErrorBoundary>
              <SavedListingsPage />
            </MarketplaceErrorBoundary>
          </RequirePublicPage>
        }
      />
      <Route
        path="/marketplace/saved-searches"
        element={
          <RequirePublicPage pageKey="marketplace">
            <RequireUserAuth>
              <MarketplaceErrorBoundary>
                <MarketplaceSavedSearchesPage />
              </MarketplaceErrorBoundary>
            </RequireUserAuth>
          </RequirePublicPage>
        }
      />
      <Route
        path="/marketplace/messages"
        element={
          <RequirePublicPage pageKey="marketplace">
            <RequireUserAuth>
              <MarketplaceErrorBoundary>
                <MarketplaceMessagesPage />
              </MarketplaceErrorBoundary>
            </RequireUserAuth>
          </RequirePublicPage>
        }
      />
      <Route
        path="/marketplace/reviews"
        element={
          <RequirePublicPage pageKey="marketplace">
            <RequireUserAuth>
              <MarketplaceErrorBoundary>
                <MarketplaceReviewsPage />
              </MarketplaceErrorBoundary>
            </RequireUserAuth>
          </RequirePublicPage>
        }
      />
      <Route
        path="/marketplace/seller/:id"
        element={
          <RequirePublicPage pageKey="marketplace">
            <MarketplaceErrorBoundary>
              <SellerStorefrontPage />
            </MarketplaceErrorBoundary>
          </RequirePublicPage>
        }
      />
      <Route
        path="/marketplace/:id"
        element={
          <RequirePublicPage pageKey="marketplace">
            <MarketplaceErrorBoundary>
              <ListingDetailPage />
            </MarketplaceErrorBoundary>
          </RequirePublicPage>
        }
      />
      <Route path="/explore" element={<Navigate to="/" replace />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route
        path="/onboarding/shopping-profile"
        element={
          <RequireUserAuth>
            <ShoppingProfileOnboardingPage />
          </RequireUserAuth>
        }
      />
      <Route
        path="/community"
        element={
          <RequirePublicPage pageKey="feed">
            <RequireUserAuth>
              <FeedPage />
            </RequireUserAuth>
          </RequirePublicPage>
        }
      />
      <Route path="/feed" element={<Navigate to="/community" replace />} />
      <Route
        path="/flex"
        element={
          <RequirePublicPage pageKey="feed">
            <Navigate to="/community" replace />
          </RequirePublicPage>
        }
      />
      <Route path="/login" element={<UserLoginPage />} />
      <Route
        path="/register"
        element={<Navigate to="/login?mode=signup" replace />}
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route
        path="/profile"
        element={
          <RequireUserAuth>
            <ProfileToUserRedirect />
          </RequireUserAuth>
        }
      />
      <Route
        path="/profile/*"
        element={
          <RequireUserAuth>
            <ProfileToUserRedirect />
          </RequireUserAuth>
        }
      />
      <Route
        path="/saved-searches"
        element={
          <RequireUserAuth>
            <ProfileToUserRedirect />
          </RequireUserAuth>
        }
      />
      <Route path="/user/:userId/*" element={<UserProfileLayout />} />
      <Route
        path="/users/search"
        element={
          <RequireUserAuth>
            <UserSearchPage />
          </RequireUserAuth>
        }
      />
      <Route
        path="/users/similar"
        element={<Navigate to="/users/search" replace />}
      />
    </>
  );
}
